/**
 * Signed-out → login redirect (WP0.7: SC-XC03.3, SC-AU05.7).
 *
 * The backend answers 401 (with a `WWW-Authenticate` challenge) when no
 * user / moderator session is present, and keeps 403 for "signed in but not
 * allowed" (e.g. company not approved yet). On a 401 from our API we send the
 * browser to the login page of the area the user is in:
 *   /moderator/**  → /moderator/login
 *   /b2b/**        → /b2b/login (and the persisted B2B auth state is cleared)
 * Public pages (home, login, password reset, registration) never redirect.
 *
 * Both HTTP stacks report here: the orval axios mutator (src/api/client.ts)
 * and the hand-written `fetch` helpers (src/config/api.ts), the latter via
 * `installFetchUnauthorizedRedirect()` called once from main.tsx.
 */

const B2B_LOGIN = "/b2b/login";
const MODERATOR_LOGIN = "/moderator/login";

// Pages a signed-out visitor is allowed to be on.
const PUBLIC_PATHS = new Set([
  "/",
  B2B_LOGIN,
  "/b2b/forgot-password",
  "/b2b/reset-password",
  "/b2b/register",
  MODERATOR_LOGIN,
  "/moderator/forgot-password",
  "/moderator/reset-password",
]);

let redirecting = false;

function loginPathFor(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (PUBLIC_PATHS.has(path)) return null;
  if (path === "/moderator" || path.startsWith("/moderator/")) return MODERATOR_LOGIN;
  if (path === "/b2b" || path.startsWith("/b2b/")) return B2B_LOGIN;
  return null;
}

/** Call with the HTTP status of any API response. No-op unless it is a 401. */
export function handleUnauthorizedStatus(status: number | undefined): void {
  if (status !== 401 || redirecting) return;
  const target = loginPathFor(window.location.pathname);
  if (!target) return;
  redirecting = true;
  if (target === B2B_LOGIN) {
    localStorage.removeItem("auth-storage");
  }
  window.location.assign(target);
}

function isApiRequest(input: RequestInfo | URL): boolean {
  const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  try {
    return new URL(raw, window.location.origin).pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

/** Wrap window.fetch so 401s from `/api/...` trigger the login redirect. */
export function installFetchUnauthorizedRedirect(): void {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch(input, init);
    if (response.status === 401 && isApiRequest(input)) {
      handleUnauthorizedStatus(response.status);
    }
    return response;
  };
}
